import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Interface strings for the public site (EN/FR). Page copy is also editable per language in the
 * admin's Page Management (stored by /api/pages); those edits override the defaults here.
 * The language follows the shared i18next instance (src/i18n/config.js), so the choice is remembered.
 */
const STRINGS = {
  en: {
    'nav.residences': 'Residences',
    'nav.wellness': 'Wellness',
    'nav.dining': 'Dining',
    'nav.app': 'The App',
    'nav.about': 'About',
    'nav.contact': 'Contact',
    'nav.careers': 'Careers',
    'nav.feedback': 'Feedback',
    'nav.signin': 'Sign in',
    'nav.book': 'Book your stay',
    'nav.menu': 'Menu',
    'nav.skip': 'Skip to content',
    'brand.sub': 'Wellness residence',
    'bar.checkin': 'Check-in',
    'bar.checkout': 'Check-out',
    'bar.guests': 'Guests',
    'bar.residence': 'Residence',
    'bar.any': 'Any residence',
    'bar.check': 'Check availability',
    'bar.update': 'Update search',
    'bar.fewer': 'Fewer guests',
    'bar.more': 'More guests',
    'guest.one': 'guest',
    'guest.many': 'guests',
    'night.one': 'night',
    'night.many': 'nights',
    'price.from': 'from',
    'price.night': '/ night',
    'price.total': 'Total (XAF)',
    'res.all': 'All',
    'res.kicker': 'A Faysha residence',
    'res.details': 'Details',
    'res.reserve': 'Reserve',
    'res.available': 'available',
    'res.soldout': 'Sold out for these dates',
    'res.none': 'No residence matches these dates and guests. Try other dates or fewer guests.',
    'res.tour': '360° tour',
    'res.startTour': 'Start 360° tour',
    'res.photos': 'All photos',
    'res.inside': 'In the residence',
    'res.includes': 'Included with your stay',
    'res.beds': 'bedrooms',
    'res.bed': 'bedroom',
    'res.baths': 'baths',
    'res.bath': 'bath',
    'res.studio': 'Studio',
    'res.upto': 'Up to',
    'res.checkin': 'Check-in',
    'res.checkout': 'Check-out',
    'res.cancel': 'Cancellation',
    'res.cancelV': '14+ days: full refund',
    'res.cancelS': '7–14 days before: 50% · under 7 days: no refund.',
    'res.selfcheckin': 'Self check-in with your digital key',
    'res.leave': 'Leave your key card in the deposit box',
    'res.readterms': 'Read the terms of service',
    'res.nocharge': 'You won’t be charged until you accept the terms and confirm.',
    'res.beforetax': 'before taxes',
    'book.step1': 'Your details',
    'book.step2': 'Terms of stay',
    'book.step3': 'Payment',
    'book.step4': 'Confirmed',
    'book.who': 'Who’s staying?',
    'book.name': 'Full name',
    'book.email': 'Email',
    'book.phone': 'Phone (for your key by SMS)',
    'book.requests': 'Anything we should prepare?',
    'book.requestsPh': 'Allergies, celebrations, a crib, a quiet floor…',
    'book.terms': 'Terms of stay',
    'book.termsLead': 'Please read and accept before payment. We keep a dated record of your acceptance with your booking.',
    'book.accept1': 'I have read and accept the Terms of Service and the Privacy Policy.',
    'book.accept2': 'I agree to the house rules and the deposit box policy.',
    'book.acceptBoth': 'Accept both to continue',
    'book.payment': 'Payment',
    'book.card': 'Card',
    'book.mtn': 'MTN Mobile Money',
    'book.orange': 'Orange Money',
    'book.cardNote': 'Card payments are processed by Stripe. Faysha never stores your card number.',
    'book.momoNumber': 'Mobile money number',
    'book.momoNote': 'We record your request and our team confirms your booking as soon as the payment is received. Your digital key is released on confirmation.',
    'book.back': 'Back',
    'book.continue': 'Continue',
    'book.pay': 'Pay & confirm',
    'book.request': 'Request payment',
    'book.working': 'One moment…',
    'book.ready': 'Your residence is ready',
    'book.pending': 'Your booking is reserved',
    'book.sentTo': 'confirmation sent to',
    'book.emailLater': 'we will email your confirmation and key to',
    'book.key': 'Digital key',
    'book.keyNote': 'Sent by email and SMS before check-in, and shown in the Faysha app.',
    'book.keyPending': 'Released as soon as we confirm your mobile money payment.',
    'book.box': 'Deposit box',
    'book.boxNote': 'Your box number and code appear in the app with your key.',
    'book.app': 'Get the Faysha app',
    'book.appNote': 'Sign in with this email to open your door, book wellness and chat with your concierge.',
    'book.summary': 'Booking summary',
    'book.nights': 'Nights',
    'book.unit': 'Residence',
    'book.missing': 'Please fill in your name, email and phone.',
    'book.noUnit': 'This residence is no longer available for these dates.',
    'book.secure': 'Secure checkout',
    'book.change': 'Change dates',
    'book.cardMissing': 'Card payments are not configured yet. Please choose mobile money or contact us.',
    'common.send': 'Send',
    'common.sending': 'Sending…',
    'common.loading': 'Loading',
    'common.error': 'Something went wrong. Please try again.',
    'footer.stay': 'Stay',
    'footer.faysha': 'Faysha',
    'footer.visit': 'Visit',
    'footer.rights': 'Faysha Wellness Residence',
    'footer.privacy': 'Privacy',
    'footer.terms': 'Terms of service',
    'footer.staff': 'Staff sign in',
    'nav.events': "Events",
    'nav.stories': "Journal",
    'common.back': "Back",
    'common.continue': "Continue",
    'stories.kicker': "The journal",
    'stories.title': "Stories from Faysha",
    'stories.lead': "Weddings under the palms, retreats that reset a team, birthdays worth remembering — told by the people who hosted them.",
    'stories.all': "All stories",
    'stories.wedding': "Weddings",
    'stories.corporate': "Corporate",
    'stories.celebration': "Celebrations",
    'stories.retreat': "Retreats",
    'stories.wellness': "Wellness",
    'stories.news': "News",
    'stories.guests': "guests",
    'stories.empty': "Our first stories are being written. In the meantime, tell us about yours.",
    'stories.notFound': "This story could not be found.",
    'stories.back': "Back to the journal",
    'stories.more': "More stories",
    'stories.yourTurn': "Your celebration, our next story",
    'events.kicker': "Events at Faysha",
    'events.title': "Celebrations as considered as the residences",
    'events.lead': "Weddings, milestone birthdays, corporate retreats and private dinners — hosted in our gardens, by the pool or across the whole estate, with our kitchen, our team and our residences for your guests.",
    'events.cta': "Plan your event",
    'events.howKicker': "How it works",
    'events.howTitle': "Tailored, not packaged",
    'events.howLead': "No two events are alike, so we do not publish fixed prices. Tell us what you imagine; we assess it with you and send a proposal built around your day.",
    'events.how1': "Tell us about it",
    'events.how1d': "A few questions about the occasion, the guests, the spaces and the services you would like.",
    'events.how2': "We assess it together",
    'events.how2d': "Our events team calls you within one working day to understand the details and, if you wish, invites you to visit.",
    'events.how3': "Your proposal",
    'events.how3d': "A tailored proposal by email — every line explained. Accept it online in a minute.",
    'events.how4': "Your date is yours",
    'events.how4d': "A deposit secures the date. From then on one coordinator looks after everything until the last guest leaves.",
    'events.formKicker': "Start here",
    'events.formTitle': "Plan your event",
    'events.step1': "The occasion",
    'events.step2': "Spaces & food",
    'events.step3': "Services & budget",
    'events.step4': "Your details",
    'events.qType': "What are you celebrating?",
    'events.qDate': "Preferred date",
    'events.qAlt': "Alternative date",
    'events.qGuests': "Number of guests",
    'events.qStart': "Starts at",
    'events.qEnd': "Ends at",
    'events.qFlexible': "My dates are flexible",
    'events.qSpaces': "Where would you like it?",
    'events.qCatering': "Food and drinks",
    'events.qStyle': "Style of service",
    'events.qDietary': "Dietary needs",
    'events.qDietaryPh': "Vegetarian guests, allergies, halal…",
    'events.qServices': "What else should we take care of?",
    'events.qRooms': "Residences for overnight guests",
    'events.qBudgetMin': "Budget from",
    'events.qBudgetMax': "Budget up to",
    'events.qCurrency': "Currency",
    'events.qVision': "Describe the day you imagine",
    'events.qVisionPh': "The atmosphere, the timeline, anything important to you…",
    'events.qOrg': "Company or organisation (optional)",
    'events.qHeard': "How did you hear about us? (optional)",
    'events.send': "Send my request",
    'events.pricingNote': "Every event is priced after we assess it with you. Sending this request commits you to nothing.",
    'events.errType': "Choose the kind of event",
    'events.errDate': "Choose a date, or tick “My dates are flexible”",
    'events.errGuests': "How many guests are you expecting?",
    'events.errContact': "Please give your name and a valid email",
    'events.thanksTitle': "Thank you — we will be in touch",
    'events.thanksLead': "Our events team will call or write within one working day. A confirmation is on its way to your inbox.",
    'events.storiesTitle': "Recently hosted",
    'events.proposal': "Proposal",
    'events.proposalFor': "Prepared for {name}",
    'events.discount': "Discount",
    'events.tax': "Tax",
    'events.total': "Total",
    'events.deposit': "Deposit to reserve the date",
    'events.validUntil': "Valid until",
    'events.signName': "Your full name, to accept",
    'events.noteToUs': "A note for our team (optional)",
    'events.accept': "Accept the proposal",
    'events.decline': "Decline",
    'events.acceptNote': "Accepting reserves nothing yet: your coordinator will send payment details, and the date is secured once the deposit arrives.",
    'events.state_accepted': "You accepted this proposal — thank you. Your coordinator will be in touch with payment details for the deposit.",
    'events.state_declined': "You declined this proposal. If you would like changes, simply reply to our email.",
    'events.state_superseded': "This proposal has been replaced by a newer version — please open the latest email from us.",
    'events.state_expired': "This proposal has expired. Please contact us for an updated one.",
    'events.wedding': "Wedding",
    'events.engagement': "Engagement",
    'events.birthday': "Birthday",
    'events.anniversary': "Anniversary",
    'events.corporate': "Corporate",
    'events.conference': "Conference",
    'events.retreat': "Retreat",
    'events.private_dinner': "Private dinner",
    'events.other': "Something else",
    'events.garden': "Gardens",
    'events.pool_deck': "Pool deck",
    'events.restaurant': "Restaurant",
    'events.sky_deck': "Sky deck",
    'events.lounge': "Lounge",
    'events.wellness_club': "Wellness club",
    'events.whole_estate': "The whole estate",
    'events.faysha': "Our restaurant",
    'events.external': "My own caterer",
    'events.none': "No catering",
    'events.plated': "Plated dinner",
    'events.buffet': "Buffet",
    'events.cocktail': "Cocktail",
    'events.family': "Family style",
    'events.bbq': "Barbecue",
    'events.decor': "Decor",
    'events.flowers': "Flowers",
    'events.sound': "Sound & DJ",
    'events.lighting': "Lighting",
    'events.photo': "Photography",
    'events.video': "Video",
    'events.cake': "Cake",
    'events.officiant': "Officiant",
    'events.transport': "Guest transport",
    'events.security': "Security",
    'events.wellness': "Spa for the party",
    'events.kids': "Children’s corner",
  },
  fr: {
    'nav.residences': 'Résidences',
    'nav.wellness': 'Bien-être',
    'nav.dining': 'Restauration',
    'nav.app': 'L’application',
    'nav.about': 'À propos',
    'nav.contact': 'Contact',
    'nav.careers': 'Carrières',
    'nav.feedback': 'Avis',
    'nav.signin': 'Connexion',
    'nav.book': 'Réserver',
    'nav.menu': 'Menu',
    'nav.skip': 'Aller au contenu',
    'brand.sub': 'Résidence bien-être',
    'bar.checkin': 'Arrivée',
    'bar.checkout': 'Départ',
    'bar.guests': 'Voyageurs',
    'bar.residence': 'Résidence',
    'bar.any': 'Toutes les résidences',
    'bar.check': 'Voir les disponibilités',
    'bar.update': 'Mettre à jour',
    'bar.fewer': 'Moins de voyageurs',
    'bar.more': 'Plus de voyageurs',
    'guest.one': 'voyageur',
    'guest.many': 'voyageurs',
    'night.one': 'nuit',
    'night.many': 'nuits',
    'price.from': 'à partir de',
    'price.night': '/ nuit',
    'price.total': 'Total (XAF)',
    'res.all': 'Toutes',
    'res.kicker': 'Une résidence Faysha',
    'res.details': 'Détails',
    'res.reserve': 'Réserver',
    'res.available': 'disponible(s)',
    'res.soldout': 'Complet à ces dates',
    'res.none': 'Aucune résidence ne correspond à ces dates. Essayez d’autres dates ou moins de voyageurs.',
    'res.tour': 'Visite 360°',
    'res.startTour': 'Lancer la visite 360°',
    'res.photos': 'Toutes les photos',
    'res.inside': 'Dans la résidence',
    'res.includes': 'Inclus dans votre séjour',
    'res.beds': 'chambres',
    'res.bed': 'chambre',
    'res.baths': 'salles de bain',
    'res.bath': 'salle de bain',
    'res.studio': 'Studio',
    'res.upto': 'Jusqu’à',
    'res.checkin': 'Arrivée',
    'res.checkout': 'Départ',
    'res.cancel': 'Annulation',
    'res.cancelV': '14 jours et plus : remboursé',
    'res.cancelS': '7 à 14 jours avant : 50 % · moins de 7 jours : aucun remboursement.',
    'res.selfcheckin': 'Arrivée autonome avec votre clé numérique',
    'res.leave': 'Déposez votre carte dans le coffre de dépôt',
    'res.readterms': 'Lire les conditions générales',
    'res.nocharge': 'Aucun débit avant l’acceptation des conditions et la confirmation.',
    'res.beforetax': 'hors taxes',
    'book.step1': 'Vos informations',
    'book.step2': 'Conditions de séjour',
    'book.step3': 'Paiement',
    'book.step4': 'Confirmé',
    'book.who': 'Qui séjourne ?',
    'book.name': 'Nom complet',
    'book.email': 'E-mail',
    'book.phone': 'Téléphone (clé par SMS)',
    'book.requests': 'Que pouvons-nous préparer ?',
    'book.requestsPh': 'Allergies, célébration, lit bébé, étage calme…',
    'book.terms': 'Conditions de séjour',
    'book.termsLead': 'Veuillez lire et accepter avant le paiement. Votre acceptation est enregistrée et datée avec votre réservation.',
    'book.accept1': 'J’ai lu et j’accepte les Conditions générales et la Politique de confidentialité.',
    'book.accept2': 'J’accepte le règlement intérieur et la politique du coffre de dépôt.',
    'book.acceptBoth': 'Acceptez les deux pour continuer',
    'book.payment': 'Paiement',
    'book.card': 'Carte',
    'book.mtn': 'MTN Mobile Money',
    'book.orange': 'Orange Money',
    'book.cardNote': 'Les paiements par carte sont traités par Stripe. Faysha ne conserve jamais votre numéro de carte.',
    'book.momoNumber': 'Numéro mobile money',
    'book.momoNote': 'Nous enregistrons votre demande et notre équipe confirme votre réservation dès réception du paiement. Votre clé numérique est envoyée à la confirmation.',
    'book.back': 'Retour',
    'book.continue': 'Continuer',
    'book.pay': 'Payer et confirmer',
    'book.request': 'Demander le paiement',
    'book.working': 'Un instant…',
    'book.ready': 'Votre résidence vous attend',
    'book.pending': 'Votre réservation est retenue',
    'book.sentTo': 'confirmation envoyée à',
    'book.emailLater': 'nous enverrons la confirmation et la clé à',
    'book.key': 'Clé numérique',
    'book.keyNote': 'Envoyée par e-mail et SMS avant l’arrivée, et visible dans l’application Faysha.',
    'book.keyPending': 'Envoyée dès la confirmation de votre paiement mobile money.',
    'book.box': 'Coffre de dépôt',
    'book.boxNote': 'Le numéro et le code apparaissent dans l’application avec votre clé.',
    'book.app': 'Téléchargez l’application Faysha',
    'book.appNote': 'Connectez-vous avec cet e-mail pour ouvrir votre porte, réserver le bien-être et écrire au concierge.',
    'book.summary': 'Récapitulatif',
    'book.nights': 'Nuits',
    'book.unit': 'Résidence',
    'book.missing': 'Veuillez indiquer votre nom, e-mail et téléphone.',
    'book.noUnit': 'Cette résidence n’est plus disponible à ces dates.',
    'book.secure': 'Paiement sécurisé',
    'book.change': 'Modifier les dates',
    'book.cardMissing': 'Le paiement par carte n’est pas encore activé. Choisissez le mobile money ou contactez-nous.',
    'common.send': 'Envoyer',
    'common.sending': 'Envoi…',
    'common.loading': 'Chargement',
    'common.error': 'Une erreur est survenue. Veuillez réessayer.',
    'footer.stay': 'Séjour',
    'footer.faysha': 'Faysha',
    'footer.visit': 'Nous rendre visite',
    'footer.rights': 'Faysha Wellness Residence',
    'footer.privacy': 'Confidentialité',
    'footer.terms': 'Conditions générales',
    'footer.staff': 'Espace équipe',
    'nav.events': "Événements",
    'nav.stories': "Journal",
    'common.back': "Retour",
    'common.continue': "Continuer",
    'stories.kicker': "Le journal",
    'stories.title': "Histoires de Faysha",
    'stories.lead': "Des mariages sous les palmiers, des retraites qui ressoudent une équipe, des anniversaires inoubliables — racontés par ceux qui les ont accueillis.",
    'stories.all': "Toutes les histoires",
    'stories.wedding': "Mariages",
    'stories.corporate': "Entreprise",
    'stories.celebration': "Célébrations",
    'stories.retreat': "Retraites",
    'stories.wellness': "Bien-être",
    'stories.news': "Actualités",
    'stories.guests': "invités",
    'stories.empty': "Nos premières histoires sont en cours d’écriture. En attendant, parlez-nous de la vôtre.",
    'stories.notFound': "Cette histoire est introuvable.",
    'stories.back': "Retour au journal",
    'stories.more': "Autres histoires",
    'stories.yourTurn': "Votre célébration, notre prochaine histoire",
    'events.kicker': "Événements à Faysha",
    'events.title': "Des célébrations aussi soignées que nos résidences",
    'events.lead': "Mariages, anniversaires, séminaires et dîners privés — dans nos jardins, au bord de la piscine ou sur tout le domaine, avec notre cuisine, notre équipe et nos résidences pour vos invités.",
    'events.cta': "Organiser votre événement",
    'events.howKicker': "Comment ça marche",
    'events.howTitle': "Sur mesure, jamais en formule",
    'events.howLead': "Aucun événement ne ressemble à un autre : nous ne publions donc pas de prix fixes. Dites-nous ce que vous imaginez ; nous l’étudions avec vous et vous envoyons une proposition pensée pour votre journée.",
    'events.how1': "Racontez-nous",
    'events.how1d': "Quelques questions sur l’occasion, les invités, les espaces et les services souhaités.",
    'events.how2': "Nous l’étudions ensemble",
    'events.how2d': "Notre équipe vous appelle sous un jour ouvré pour préciser les détails et, si vous le souhaitez, vous inviter à visiter.",
    'events.how3': "Votre proposition",
    'events.how3d': "Une proposition sur mesure par e-mail — chaque ligne expliquée. Acceptez-la en ligne en une minute.",
    'events.how4': "Votre date est réservée",
    'events.how4d': "Un acompte réserve la date. Ensuite, un coordinateur s’occupe de tout jusqu’au départ du dernier invité.",
    'events.formKicker': "Commencez ici",
    'events.formTitle': "Organiser votre événement",
    'events.step1': "L’occasion",
    'events.step2': "Espaces & repas",
    'events.step3': "Services & budget",
    'events.step4': "Vos coordonnées",
    'events.qType': "Que célébrez-vous ?",
    'events.qDate': "Date souhaitée",
    'events.qAlt': "Autre date possible",
    'events.qGuests': "Nombre d’invités",
    'events.qStart': "Début",
    'events.qEnd': "Fin",
    'events.qFlexible': "Mes dates sont flexibles",
    'events.qSpaces': "Où souhaitez-vous le tenir ?",
    'events.qCatering': "Repas et boissons",
    'events.qStyle': "Type de service",
    'events.qDietary': "Régimes alimentaires",
    'events.qDietaryPh': "Végétariens, allergies, halal…",
    'events.qServices': "De quoi d’autre pouvons-nous nous occuper ?",
    'events.qRooms': "Résidences pour les invités qui restent",
    'events.qBudgetMin': "Budget à partir de",
    'events.qBudgetMax': "Budget jusqu’à",
    'events.qCurrency': "Devise",
    'events.qVision': "Décrivez la journée que vous imaginez",
    'events.qVisionPh': "L’ambiance, le déroulé, ce qui compte pour vous…",
    'events.qOrg': "Entreprise ou organisation (facultatif)",
    'events.qHeard': "Comment nous avez-vous connus ? (facultatif)",
    'events.send': "Envoyer ma demande",
    'events.pricingNote': "Chaque événement est chiffré après l’avoir étudié avec vous. Cette demande ne vous engage à rien.",
    'events.errType': "Choisissez le type d’événement",
    'events.errDate': "Choisissez une date, ou cochez « Mes dates sont flexibles »",
    'events.errGuests': "Combien d’invités attendez-vous ?",
    'events.errContact': "Indiquez votre nom et un e-mail valide",
    'events.thanksTitle': "Merci — nous revenons vers vous",
    'events.thanksLead': "Notre équipe vous appelle ou vous écrit sous un jour ouvré. Une confirmation arrive dans votre boîte mail.",
    'events.storiesTitle': "Récemment accueillis",
    'events.proposal': "Proposition",
    'events.proposalFor': "Préparée pour {name}",
    'events.discount': "Remise",
    'events.tax': "Taxe",
    'events.total': "Total",
    'events.deposit': "Acompte pour réserver la date",
    'events.validUntil': "Valable jusqu’au",
    'events.signName': "Votre nom complet, pour accepter",
    'events.noteToUs': "Un mot pour notre équipe (facultatif)",
    'events.accept': "Accepter la proposition",
    'events.decline': "Refuser",
    'events.acceptNote': "Accepter ne réserve encore rien : votre coordinateur vous enverra les modalités de paiement, et la date est réservée à réception de l’acompte.",
    'events.state_accepted': "Vous avez accepté cette proposition — merci. Votre coordinateur vous contactera pour l’acompte.",
    'events.state_declined': "Vous avez refusé cette proposition. Pour des modifications, répondez simplement à notre e-mail.",
    'events.state_superseded': "Cette proposition a été remplacée par une version plus récente — ouvrez notre dernier e-mail.",
    'events.state_expired': "Cette proposition a expiré. Contactez-nous pour une version à jour.",
    'events.wedding': "Mariage",
    'events.engagement': "Fiançailles",
    'events.birthday': "Anniversaire",
    'events.anniversary': "Anniversaire de mariage",
    'events.corporate': "Entreprise",
    'events.conference': "Conférence",
    'events.retreat': "Retraite",
    'events.private_dinner': "Dîner privé",
    'events.other': "Autre chose",
    'events.garden': "Jardins",
    'events.pool_deck': "Bord de piscine",
    'events.restaurant': "Restaurant",
    'events.sky_deck': "Sky deck",
    'events.lounge': "Salon",
    'events.wellness_club': "Club bien-être",
    'events.whole_estate': "Tout le domaine",
    'events.faysha': "Notre restaurant",
    'events.external': "Mon traiteur",
    'events.none': "Sans restauration",
    'events.plated': "Dîner servi à table",
    'events.buffet': "Buffet",
    'events.cocktail': "Cocktail",
    'events.family': "Plats à partager",
    'events.bbq': "Barbecue",
    'events.decor': "Décoration",
    'events.flowers': "Fleurs",
    'events.sound': "Son & DJ",
    'events.lighting': "Éclairage",
    'events.photo': "Photographie",
    'events.video': "Vidéo",
    'events.cake': "Gâteau",
    'events.officiant': "Officiant",
    'events.transport': "Transport des invités",
    'events.security': "Sécurité",
    'events.wellness': "Spa pour les proches",
    'events.kids': "Espace enfants",
  },
};

export function useLang() {
  const { i18n } = useTranslation();
  const lang = (i18n.language || 'en').slice(0, 2) === 'fr' ? 'fr' : 'en';
  const t = useCallback(
    (key, vars) => {
      const text = STRINGS[lang][key] ?? STRINGS.en[key] ?? key;
      return vars ? text.replace(/\{(\w+)\}/g, (m, k) => (vars[k] ?? m)) : text;
    },
    [lang],
  );
  const setLang = useCallback((l) => i18n.changeLanguage(l), [i18n]);
  return { lang, t, setLang };
}

export const plural = (t, n, one, many) => `${n} ${t(n === 1 ? one : many)}`;
